'use client';

import React from 'react';

import { cn } from '@/lib/utils';

export interface ChartTipRow {
  key: string;
  label: React.ReactNode;
  value: React.ReactNode;
  /** Swatch colour. Omit for a single-series chart, where identity is the title. */
  color?: string;
}

export interface ChartTipProps {
  label?: React.ReactNode;
  rows: ChartTipRow[];
  className?: string;
}

/**
 * The one tooltip shell. Values wear text tokens, never the series colour: a
 * small swatch beside the label carries identity, so the number stays legible
 * on both surfaces.
 */
export default function ChartTip({ label, rows, className }: ChartTipProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-ds-border-subtlest bg-ds-surface-white px-3 py-2 shadow-md',
        className
      )}
    >
      {label ? (
        <p className="ds-text-body-xs-medium text-ds-text-subtle">{label}</p>
      ) : null}
      <div className={cn('flex flex-col gap-1', label && 'mt-1')}>
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-2">
            {row.color ? (
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-sm"
                style={{ backgroundColor: row.color }}
              />
            ) : null}
            {rows.length > 1 ? (
              <span className="ds-text-body-xs-medium text-ds-text-subtle">
                {row.label}
              </span>
            ) : null}
            <span className="ds-text-body-sm-semibold text-ds-text-darker tabular-nums ml-auto">
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
